// React-i18next compatible implementation
import enTranslation from '../locales/en/translation.json';
import hiTranslation from '../locales/hi/translation.json';
import enAdmin from '../locales/en/admin.json';
import hiAdmin from '../locales/hi/admin.json';

type Language = 'en' | 'hi';
type TranslationObject = Record<string, any>;
type Translations = Record<Language, TranslationObject>;

interface TranslationOptions {
  fallback?: string;
  returnObjects?: boolean;
}

type LanguageChangeCallback = (language: Language) => void;

// Merge landing and admin translations
const translations: Translations = {
  en: { ...enTranslation, ...enAdmin },
  hi: { ...hiTranslation, ...hiAdmin }
};

class I18nInstance {
  private currentLanguage: Language;
  private listeners: LanguageChangeCallback[] = [];

  constructor() {
    this.currentLanguage = this.getStoredLanguage() || this.detectBrowserLanguage() || 'en';
    this.updateHtmlLang();
  }

  private getStoredLanguage(): Language | null {
    try {
      const stored = localStorage.getItem('i18nextLng');
      if (stored === 'en' || stored === 'hi') {
        return stored as Language;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  private detectBrowserLanguage(): Language {
    const browserLang = navigator.language || (navigator.languages && navigator.languages[0]);
    if (browserLang && browserLang.startsWith('hi')) return 'hi';
    return 'en';
  }

  private updateHtmlLang(): void {
    document.documentElement.setAttribute('lang', this.currentLanguage);
  }

  changeLanguage(lng: Language): Promise<void> {
    if (lng !== this.currentLanguage && (lng === 'en' || lng === 'hi')) {
      this.currentLanguage = lng;
      try {
        localStorage.setItem('i18nextLng', lng);
      } catch (e) {
        console.warn('Could not save language preference to localStorage');
      }
      this.updateHtmlLang();
      this.notifyListeners();
    }
    return Promise.resolve();
  }

  t(key: string, options: TranslationOptions = {}): string | string[] {
    const fallback = options.fallback || key;
    const keys = key.split('.');
    let value: any = translations[this.currentLanguage];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found in current language
        value = translations.en;
        for (const k of keys) {
          if (value && typeof value === 'object' && k in value) {
            value = value[k];
          } else {
            return fallback;
          }
        }
        break;
      }
    }

    if (options.returnObjects && Array.isArray(value)) {
      return value;
    }

    return typeof value === 'string' ? value : (Array.isArray(value) ? value : fallback);
  }

  get language(): Language {
    return this.currentLanguage;
  }

  onLanguageChanged(callback: LanguageChangeCallback): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(callback => callback(this.currentLanguage));
  }
}

const i18n = new I18nInstance();

export default i18n;
export type { Language, TranslationOptions };
